import Service from '@ember/service';
import { service } from '@ember/service';
import StaticParts from '../data/local-parts';
import WhyNot from '../models/component-selector/why-not';
import * as Format from '../utils/format';
import FilterConstants from '../filter-constants';
import { collectOpAmps, collectMockOpAmps, DataType as D, CardinalityType as C } from './opamps/loader';
import { applyAdditionalCriteria, reasonsOpampPackagingMarginal } from './opamps/ratings';
import { evaluateOpAmps } from '@ebiz/aditools-filter/recommendations';
import { getCircuitValues } from 'filter-wizard/models/gestures/optimization';
import { camelize } from '@ember/string';
import { set } from '@ember/object';

let set_iNoise = function (part) {
  if (
    'iBias' in part &&
    part.iBias < 80e-12 &&
    'architecture' in part &&
    -1 === part.architecture.indexOf('Zero Drift')
  ) {
    return 1e-12;
  } else {
    return null;
  }
};

const PST_Parameters = {
  partNumber: { id: 'partNumber', name: 'partNumber', data: D.t_none, value: C.c_none, optional: true },
  gbpHz: { id: 'gbpHz', name: 'gbw', data: D.t_float, value: C.c_single, optional: false },
  slewRateVus: { id: 'slewRateVus', name: 'slewRate', data: D.t_float, value: C.c_single, optional: false },
  outputImpedanceOhms: { id: 'outputImpedanceOhms', name: 'rOut', data: D.t_float, value: C.c_single, optional: false },
  supplyCurrentTyp: {
    id: 'supplyCurrentTyp',
    name: 'supplyCurrent',
    data: D.t_float,
    value: C.c_single,
    optional: false,
  },
  currentNoiseDensity: {
    id: 'currentNoiseDensity',
    name: 'iNoise',
    data: D.t_float,
    value: C.c_single,
    optional: false,
    default: set_iNoise,
  },
  vNoiseDensity: { id: 'vNoiseDensity', name: 'vNoise', data: D.t_float, value: C.c_single, optional: false },
  outVPlusHeadroomV: {
    id: 'outVPlusHeadroomV',
    name: 'outHeadroomPlus',
    data: D.t_float,
    value: C.c_single,
    optional: false,
  },
  outVMinusHeadroomV: {
    id: 'outVMinusHeadroomV',
    name: 'outHeadroomMinus',
    data: D.t_float,
    value: C.c_single,
    optional: false,
  },
  inVPlusHeadroomV: {
    id: 'inVPlusHeadroomV',
    name: 'inHeadroomPlus',
    data: D.t_float,
    value: C.c_single,
    optional: false,
  },
  inVMinusHeadroomV: {
    id: 'inVMinusHeadroomV',
    name: 'inHeadroomMinus',
    data: D.t_float,
    value: C.c_single,
    optional: false,
  },
  feedbackType: { id: 'feedbackType', name: 'feedbackMethod', data: D.t_pulldown, value: C.c_single, optional: true },
  inputArchitecture: {
    id: 'inputArchitecture',
    name: 'architecture',
    data: D.t_pulldown,
    value: C.c_multi,
    optional: true,
  },
  vsSpanMinV: { id: 'vsSpanMinV', name: 'minSupplyVoltage', data: D.t_float, value: C.c_single, optional: false },
  vsSpanMaxV: { id: 'vsSpanMaxV', name: 'maxSupplyVoltage', data: D.t_float, value: C.c_single, optional: false },
  numberOfChannels: {
    id: 'numberOfChannels',
    name: 'numberChannels',
    data: D.t_integer,
    value: C.c_single,
    optional: false,
  },
  packageCode: { id: 'packageCode', name: 'packageCode', data: D.t_pulldown, value: C.c_multi, optional: false },
  minStableGainVV: {
    id: 'minStableGainVV',
    name: 'minStableGain',
    data: D.t_float,
    value: C.c_single,
    optional: false,
  },
  ccmF: { id: 'ccmF', name: 'cCm', data: D.t_float, value: C.c_single, optional: true },
  cDiffF: { id: 'cDiffF', name: 'cDiff', data: D.t_float, value: C.c_single, optional: true },
  ibiasMaxA: { id: 'ibiasMaxA', name: 'iBias', data: D.t_float, value: C.c_single, optional: false },
  vosMaxV: { id: 'vosMaxV', name: 'vos', data: D.t_float, value: C.c_single, optional: true },
  vintage: { id: 'vintage', name: 'vintage', data: D.t_integer, value: C.c_single, optional: true },
  statusText: { id: 'statusText', name: 'statusText', data: D.t_string, value: C.c_single, optional: true },
  status: { id: 'status', name: 'status', data: D.t_integer, value: C.c_single, optional: true },
  price: { id: 'price', name: 'price', data: D.t_float, value: C.c_single, optional: true },
  temperature: { id: 'temperature', name: 'tempRange', data: D.t_string, value: C.c_single, optional: true },
};

let buildOpAmpDisplayItem = function (opAmp, selectable, status, commentList, canOrderEvalBoard, _intl) {
  let opAmpItem = {
    partNumber: opAmp.partNumber,
    gbw: Format.formatFieldValue(parseFloat(opAmp.gbw), FilterConstants.fieldTypes.Engineering, '3s'),
    rawGBW: opAmp.gbw,
    minSupplyVoltage: Format.formatFieldValue(
      parseFloat(opAmp.minSupplyVoltage),
      FilterConstants.fieldTypes.Engineering,
      '3s',
    ),
    maxSupplyVoltage: Format.formatFieldValue(
      parseFloat(opAmp.maxSupplyVoltage),
      FilterConstants.fieldTypes.Engineering,
      '3s',
    ),
    feedbackMethod: opAmp.feedbackMethod
      ? opAmp.feedbackMethod
      : opAmp.architecture.indexOf('Current Feedback') > -1
        ? 'Current'
        : 'Voltage',
    iBias: !isNaN(parseFloat(opAmp.iBias))
      ? Format.formatFieldValue(parseFloat(opAmp.iBias), FilterConstants.fieldTypes.Engineering, '3s')
      : '-',
    vNoise: Format.formatFieldValue(parseFloat(opAmp.vNoise), FilterConstants.fieldTypes.Engineering, '3s'),
    supplyCurrent: Format.formatFieldValue(
      parseFloat(opAmp.supplyCurrent),
      FilterConstants.fieldTypes.Engineering,
      '3s',
    ),
    vos: !isNaN(parseFloat(opAmp.vos))
      ? Format.formatFieldValue(parseFloat(opAmp.vos), FilterConstants.fieldTypes.Engineering, '3s')
      : '-',
    numberChannels: Format.formatFieldValue(
      parseFloat(opAmp.numberChannels),
      FilterConstants.fieldTypes.Engineering,
      '3s',
    ),
    outHeadroomPlus: Format.formatFieldValue(
      parseFloat(opAmp.outHeadroomPlus),
      FilterConstants.fieldTypes.Engineering,
      '3s',
    ),
    outHeadroomMinus: Format.formatFieldValue(
      parseFloat(opAmp.outHeadroomMinus),
      FilterConstants.fieldTypes.Engineering,
      '3s',
    ),
    inHeadroomPlus: Format.formatFieldValue(
      parseFloat(opAmp.inHeadroomPlus),
      FilterConstants.fieldTypes.Engineering,
      '3s',
    ),
    inHeadroomMinus: Format.formatFieldValue(
      parseFloat(opAmp.inHeadroomMinus),
      FilterConstants.fieldTypes.Engineering,
      '3s',
    ),
    statusText: opAmp.statusText,
    isLTSpiceOpAmp: 'spice' in opAmp ? 'Yes' : 'No',
    price: '$' + parseFloat(opAmp.price).toFixed(2),
    selectable: selectable,
    status: status,
    comments: buildComments(commentList, _intl),
    commentList: commentList,
    disallowed: commentList[0].messageId === 'opAmpSelection.status.not-allowed',
    canOrderEvalBoard: canOrderEvalBoard,
    vintage: opAmp.vintage,
    tempRange: opAmp.tempRange === '' ? ' ' : opAmp.tempRange,
  };

  return opAmpItem;
};

let buildComments = function (commentList, intl) {
  let translatedElements = commentList.map((comment) => {
    return intl.t(comment.messageId, { value: comment.value }).toString();
  });
  if (translatedElements.length > 1) {
    return translatedElements[0] + ': ' + translatedElements.slice(1).join(', ');
  } else {
    return translatedElements[0];
  }
};

class SelectionTableFilterOptions {
  constructor() {
    this.columns = undefined;
    this.filters = undefined;
    this.sortColumns = undefined;
  }
  columns;
  filters;
  sortColumns;
}

// ------ The service that interacts with the rest of the program ------

export default class OpAmpsService extends Service {
  @service('aditools-site') siteconfig;
  @service fastboot;
  @service logger;
  @service cdp;
  @service intl;

  request = null; // holds the promise from the request
  opamps = [];
  databook = null;
  requiresExternalSimulation = false;
  pstData = null;
  pstViewPromise = null;
  pstView = null;
  pstMappingPromise = null;
  pstMapping = null;
  selectionFilterOptions = new SelectionTableFilterOptions();

  load() {
    this.logger.time('opampsLoadTime');
    const logger = this.logger;
    if (this.fastboot.isFastBoot) {
      this.request = Promise.resolve(null);
    } else if (this.siteconfig.OpAmpsUrl) {
      this.request = collectMockOpAmps(this.siteconfig.OpAmpsUrl, logger)
        .then((json) => {
          this.opamps = json.data;
          this.logger.timeEnd('opampsLoadTime');
          return json;
        })
        .catch((error) => {
          this.logger.error(`Load of mock opamps failed: ${error}`);
          this.logger.timeEnd('opampsLoadTime');
        });
    } else {
      this.request = collectOpAmps(
        this.siteconfig.SiteUrl,
        this.cdp,
        this.siteconfig.AdiLocale,
        'fw',
        StaticParts,
        PST_Parameters,
        logger,
        this.requiresExternalSimulation,
      )
        .then((result) => {
          this.opamps = result.opamps;
          this.logger.timeEnd('opampsLoadTime');
          this.databook = result.databook;
          return result.opamps;
        })
        .catch((error) => {
          this.logger.error(`Load of opamps failed: ${error}`);
          this.logger.timeEnd('opampsLoadTime');
        });
    }
  }

  getRequest() {
    return this.request;
  }

  getOpAmps() {
    return this.opamps;
  }

  getOpAmp(devicename) {
    return this.opamps.find(function (item) {
      return item.partNumber === devicename;
    });
  }

  getOpAmpsForDisplay(limits, posVS, negVS, implementation) {
    const modelParams = {
      circuits: limits.allowedLimits,
      individualStageInputs: {
        implementation: implementation,
      },
      vsPlus: posVS,
      vsMinus: negVS,
      optimization: limits.allowedLimits.optimization.name,
      stopbandFrequency: limits.allowedLimits.optimization.stopbandFrequency,
    };
    const paramsAllowed = this.getFilterOpampsParams(modelParams);
    const evaluatedOpAmps = evaluateOpAmps(
      paramsAllowed.opAmps,
      paramsAllowed.circuits,
      paramsAllowed.vsPlus,
      paramsAllowed.vsMinus,
      paramsAllowed.optimization,
      paramsAllowed.stopbandFrequency,
    );

    return this.opamps.map((amp) => {
      let selectable, canOrderEvalBoard, status;
      let commentList = [];
      const reasonsPackagingMarginal = reasonsOpampPackagingMarginal(amp);

      const evaluatedOpAmp = evaluatedOpAmps.find((opamp) => opamp.partNumber === amp.partNumber);
      if (evaluatedOpAmp) {
        switch (evaluatedOpAmp.status) {
          case 'notAllowed':
            status = FilterConstants.ampRecommendationStatus.notAllowed;
            commentList.push(WhyNot.create({ messageId: 'opAmpSelection.status.not-allowed' }));
            evaluatedOpAmp.reasons
              .map((reason) => {
                reason.messageId = `opAmpSelection.status.${reason.messageId}`;
                return reason;
              })
              .forEach((reason) => {
                commentList.push(reason);
              });
            selectable = false;
            canOrderEvalBoard = true;
            break;
          case 'recommended':
            if (reasonsPackagingMarginal.length === 0) {
              status = FilterConstants.ampRecommendationStatus.recommended;
              commentList.push(WhyNot.create({ messageId: 'opAmpSelection.status.recommended' }));
              canOrderEvalBoard = true;
            } else {
              // A recommended opamp becomes allowed/marginal when there are marginal reasons like
              // opamp channels, packaging, or eval board incompatibility
              status = FilterConstants.ampRecommendationStatus.marginal;
              commentList.push(WhyNot.create({ messageId: 'opAmpSelection.status.not-recommended' }));
              reasonsPackagingMarginal.forEach((reason) => {
                commentList.push(reason);
              });
              canOrderEvalBoard = reasonsPackagingMarginal.length === 0;
            }
            selectable = true;
            break;
          case 'allowed':
            status = FilterConstants.ampRecommendationStatus.marginal;
            commentList.push(WhyNot.create({ messageId: 'opAmpSelection.status.not-recommended' }));
            evaluatedOpAmp.reasons
              .map((reason) => {
                reason.messageId = `opAmpSelection.status.${reason.messageId}`;
                return reason;
              })
              .forEach((reason) => {
                commentList.push(reason);
              });

            reasonsPackagingMarginal.forEach((reason) => {
              commentList.push(reason);
            });
            selectable = true;
            canOrderEvalBoard = reasonsPackagingMarginal.length === 0;
            break;
          default:
            break;
        }
      }
      return buildOpAmpDisplayItem(amp, selectable, status, commentList, canOrderEvalBoard, this.intl);
    });
  }

  getOpAmpModelsArray(opAmpList) {
    return this.cdp.getDeviceModelData(this.getOpAmp(opAmpList.toUpperCase()).generic);
  }

  getFilterOpampsParams(model) {
    let opampList;
    if (model.opAmpsList && model.opAmpsList.length > 0) {
      opampList = model.opAmpsList.map((opamp) => {
        set(opamp, 'inHeadroomPlus', parseFloat(opamp.inHeadroomPlus));
        set(opamp, 'inHeadroomMinus', parseFloat(opamp.inHeadroomMinus));
        set(opamp, 'outHeadroomPlus', parseFloat(opamp.outHeadroomPlus));
        set(opamp, 'outHeadroomMinus', parseFloat(opamp.outHeadroomMinus));
        set(opamp, 'iNoise', parseFloat(opamp.iNoise));
        set(opamp, 'vNoise', parseFloat(opamp.vNoise));
        return opamp;
      });
    } else {
      opampList = this.opamps.map((opamp) => {
        set(opamp, 'inHeadroomPlus', parseFloat(opamp.inHeadroomPlus));
        set(opamp, 'inHeadroomMinus', parseFloat(opamp.inHeadroomMinus));
        set(opamp, 'outHeadroomPlus', parseFloat(opamp.outHeadroomPlus));
        set(opamp, 'outHeadroomMinus', parseFloat(opamp.outHeadroomMinus));
        set(opamp, 'iNoise', parseFloat(opamp.iNoise));
        set(opamp, 'vNoise', parseFloat(opamp.vNoise));
        return opamp;
      });
    }

    const circuitParams = getCircuitValues(model.circuits, model.individualStageInputs);
    let optimization = camelize(model.optimization);
    // Normalize to only provide one of the three defined values - default to power
    if (!['noise', 'voltageRange', 'power'].includes(optimization)) {
      optimization = 'power';
    }
    return {
      opAmps: opampList,
      circuits: circuitParams,
      vsPlus: model.vsPlus,
      vsMinus: model.vsMinus,
      optimization: optimization,
      stopbandFrequency: model.stopbandFrequency ?? 0,
      applyAdditionalCriteria: applyAdditionalCriteria,
    };
  }
}
